import { useEffect, useRef } from "react";

export default function HeroSection() {
  const titleRef = useRef<HTMLHeadingElement>(null);
  const subRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animRef = useRef<number>(0);

  /* ── Canvas: DNA / fractal spiral particles ── */
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    resize();
    window.addEventListener("resize", resize);

    let t = 0;

    /* colour palette — warm amber / cold silver only, no blue/purple */
    const palette = [
      "rgba(255,220,160,",
      "rgba(255,255,240,",
      "rgba(200,180,140,",
      "rgba(240,230,200,",
      "rgba(255,200,100,",
    ];

    const rand = (a: number, b: number) => a + Math.random() * (b - a);

    /* ─ Strand: one arm of the helix ─ */
    interface StrandParticle {
      offset: number;   // phase offset along helix
      alpha: number;
      size: number;
      colorIdx: number;
      speed: number;
    }

    const NUM_STRANDS = 3;
    const PARTICLES_PER_STRAND = 55;
    const strands: StrandParticle[][] = Array.from({ length: NUM_STRANDS }, (_, si) =>
      Array.from({ length: PARTICLES_PER_STRAND }, (__, pi) => ({
        offset: (pi / PARTICLES_PER_STRAND) * Math.PI * 2 * 6 + (si * Math.PI * 2) / NUM_STRANDS,
        alpha: rand(0.15, 0.65),
        size: rand(1.2, 3.2),
        colorIdx: Math.floor(Math.random() * palette.length),
        speed: rand(0.004, 0.009),
      }))
    );

    /* ─ Floating dust particles (background depth) ─ */
    interface Dust {
      x: number; y: number; r: number; alpha: number;
      vx: number; vy: number; colorIdx: number;
    }
    const DUST_COUNT = 90;
    const dust: Dust[] = Array.from({ length: DUST_COUNT }, () => ({
      x: rand(0, 1),
      y: rand(0, 1),
      r: rand(0.4, 1.8),
      alpha: rand(0.04, 0.22),
      vx: rand(-0.00012, 0.00012),
      vy: rand(-0.00008, 0.00008),
      colorIdx: Math.floor(Math.random() * palette.length),
    }));

    /* ─ Cross-strand connector lines ─ */
    const drawConnectors = (cx: number, baseLine: number, amplitude: number, twist: number) => {
      const steps = 28;
      for (let i = 0; i <= steps; i++) {
        const frac = i / steps;
        const theta = frac * Math.PI * 2 * 4 + twist;
        const y = baseLine + (frac - 0.5) * canvas.height * 1.4;
        const x1 = cx + Math.cos(theta) * amplitude;
        const x2 = cx + Math.cos(theta + Math.PI) * amplitude;
        const progress = Math.abs(Math.sin(theta));
        ctx.beginPath();
        ctx.moveTo(x1, y);
        ctx.lineTo(x2, y);
        ctx.strokeStyle = `rgba(255,240,200,${0.03 + progress * 0.07})`;
        ctx.lineWidth = 0.5;
        ctx.stroke();
      }
    };

    const draw = () => {
      const W = canvas.width;
      const H = canvas.height;
      ctx.clearRect(0, 0, W, H);

      /* soft radial glow from centre */
      const grd = ctx.createRadialGradient(W * 0.5, H * 0.5, 0, W * 0.5, H * 0.5, W * 0.7);
      grd.addColorStop(0, "rgba(255,220,140,0.04)");
      grd.addColorStop(0.5, "rgba(220,180,100,0.02)");
      grd.addColorStop(1, "rgba(0,0,0,0)");
      ctx.fillStyle = grd;
      ctx.fillRect(0, 0, W, H);

      /* dust */
      dust.forEach((d) => {
        d.x += d.vx;
        d.y += d.vy;
        if (d.x < 0) d.x = 1;
        if (d.x > 1) d.x = 0;
        if (d.y < 0) d.y = 1;
        if (d.y > 1) d.y = 0;
        ctx.beginPath();
        ctx.arc(d.x * W, d.y * H, d.r, 0, Math.PI * 2);
        ctx.fillStyle = `${palette[d.colorIdx]}${d.alpha})`;
        ctx.fill();
      });

      /* three helix columns */
      const columns = [
        { cx: W * 0.18, amp: W * 0.09, speed: 0.7 },
        { cx: W * 0.5,  amp: W * 0.13, speed: 1.0 },
        { cx: W * 0.82, amp: W * 0.09, speed: 0.85 },
      ];

      columns.forEach((col, ci) => {
        /* connectors */
        drawConnectors(col.cx, H * 0.5, col.amp, t * col.speed + ci * 1.3);

        /* strands */
        strands.forEach((strand) => {
          strand.forEach((p) => {
            const phase = p.offset + t * p.speed * col.speed;
            const yFrac = ((phase / (Math.PI * 2 * 6)) % 1 + 1) % 1;
            const x = col.cx + Math.cos(phase) * col.amp;
            const y = yFrac * H;

            /* glow halo */
            const glo = ctx.createRadialGradient(x, y, 0, x, y, p.size * 5);
            glo.addColorStop(0, `${palette[p.colorIdx]}${p.alpha * 0.9})`);
            glo.addColorStop(1, `${palette[p.colorIdx]}0)`);
            ctx.beginPath();
            ctx.arc(x, y, p.size * 5, 0, Math.PI * 2);
            ctx.fillStyle = glo;
            ctx.fill();

            /* core dot */
            ctx.beginPath();
            ctx.arc(x, y, p.size, 0, Math.PI * 2);
            ctx.fillStyle = `${palette[p.colorIdx]}${Math.min(p.alpha + 0.3, 1)})`;
            ctx.fill();
          });
        });
      });

      /* fractal ring pulses */
      for (let ring = 0; ring < 4; ring++) {
        const pulse = ((t * 0.003 + ring * 0.25) % 1);
        const r = pulse * Math.min(W, H) * 0.55;
        const alpha = (1 - pulse) * 0.07;
        ctx.beginPath();
        ctx.arc(W * 0.5, H * 0.5, r, 0, Math.PI * 2);
        ctx.strokeStyle = `rgba(255,230,180,${alpha})`;
        ctx.lineWidth = 1;
        ctx.stroke();
      }

      t += 1;
      animRef.current = requestAnimationFrame(draw);
    };

    draw();
    return () => {
      cancelAnimationFrame(animRef.current);
      window.removeEventListener("resize", resize);
    };
  }, []);

  /* ── Text fade-in ── */
  useEffect(() => {
    const el = titleRef.current;
    const sub = subRef.current;
    if (!el || !sub) return;
    el.style.opacity = "0";
    el.style.filter = "blur(24px)";
    sub.style.opacity = "0";
    const t1 = setTimeout(() => {
      el.style.transition = "opacity 3.2s ease, filter 4s ease";
      el.style.opacity = "1";
      el.style.filter = "blur(0px)";
    }, 500);
    const t2 = setTimeout(() => {
      sub.style.transition = "opacity 2.8s ease";
      sub.style.opacity = "1";
    }, 2600);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, []);

  return (
    <section
      id="hero"
      className="relative w-full h-screen overflow-hidden flex flex-col items-center justify-center"
    >
      {/* ── Background photo ── */}
      <div className="absolute inset-0 z-0">
        <img
          src="https://static.readdy.ai/image/98c8f721efe8e85064ec351cb642a7fc/63ab651c0c2397df8982eb6f6462abf0.jpeg"
          alt="MAVERICK"
          className="w-full h-full object-cover object-center"
        />
        {/* dark layer to deepen photo so particles pop */}
        <div className="absolute inset-0" style={{ background: "rgba(0,0,0,0.42)" }} />
        {/* radial vignette */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse at center, transparent 25%, rgba(0,0,0,0.55) 100%)",
          }}
        />
        {/* bottom fade */}
        <div
          className="absolute bottom-0 left-0 right-0"
          style={{
            height: "220px",
            background: "linear-gradient(to bottom, transparent 0%, #000000 100%)",
          }}
        />
      </div>

      {/* ── Canvas particles ── */}
      <canvas
        ref={canvasRef}
        className="absolute inset-0 z-10 w-full h-full pointer-events-none"
      />

      {/* ── Film grain ── */}
      <div
        className="absolute inset-0 z-10 pointer-events-none opacity-[0.09]"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='1'/%3E%3C/svg%3E\")",
          backgroundRepeat: "repeat",
          backgroundSize: "128px 128px",
        }}
      />

      {/* ── Nav ── */}
      <div className="absolute top-0 left-0 right-0 z-30 flex items-center justify-between px-6 md:px-14 py-6 md:py-8">
        <span
          className="text-white/50 font-light tracking-[0.3em] text-xs"
          style={{ fontFamily: "'Courier New', monospace" }}
        >
        </span>
        <span
          className="text-white/35 font-light tracking-widest text-xs"
          style={{ fontFamily: "'Courier New', monospace" }}
        >
          vol.5
        </span>
      </div>

      {/* ── Centre decorative helix ornament ── */}
      <div className="absolute z-20 pointer-events-none" style={{ top: "14%", left: "50%", transform: "translateX(-50%)" }}>
        <div
          style={{
            width: "1px",
            height: "60px",
            background: "linear-gradient(to bottom, transparent, rgba(255,220,160,0.4), transparent)",
            margin: "0 auto",
          }}
        />
        <div
          style={{
            width: "6px",
            height: "6px",
            borderRadius: "50%",
            background: "rgba(255,220,160,0.7)",
            margin: "0 auto",
            boxShadow: "0 0 12px 4px rgba(255,200,100,0.35)",
          }}
        />
      </div>

      {/* ── Main text ── */}
      <div className="relative z-30 flex flex-col items-center text-center px-4 w-full">

        {/* tiny label above */}
        <p
          className="text-white/30 tracking-[0.5em] text-[10px] md:text-xs mb-4 md:mb-6"
          style={{ fontFamily: "'Courier New', monospace" }}
        >
          ── 2025 ──
        </p>

        <h1
          ref={titleRef}
          className="flex items-center justify-center select-none w-full"
          style={{
            filter: "drop-shadow(0 0 40px rgba(255,220,140,0.3)) drop-shadow(0 0 80px rgba(255,200,100,0.15))",
          }}
        >
          <img
            src="https://public.readdy.ai/ai/img_res/97ee081b-88e9-4107-a425-874736cbac0a.png"
            alt="MAVERICK"
            className="w-auto"
            style={{
              maxWidth: "clamp(220px, 52vw, 780px)",
              height: "auto",
            }}
          />
        </h1>

        {/* thin rule */}
        <div
          className="mt-6 md:mt-8"
          style={{
            width: "clamp(60px, 8vw, 120px)",
            height: "1px",
            background: "linear-gradient(to right, transparent, rgba(255,220,160,0.5), transparent)",
          }}
        />

        <div ref={subRef} className="mt-5 md:mt-6 flex flex-col items-center gap-2">
          <p
            className="text-white/60 font-light tracking-[0.25em] md:tracking-[0.35em] uppercase"
            style={{
              fontSize: "clamp(11px, 2.8vw, 14px)",
              fontFamily: "'Courier New', monospace",
              textShadow: "0 0 24px rgba(255,220,140,0.35), 0 1px 3px rgba(0,0,0,0.8)",
            }}
          >
            DNA of the New Generation
          </p>
          <p
            className="text-white/35 tracking-[0.15em] md:tracking-[0.22em]"
            style={{
              fontSize: "clamp(10px, 2.2vw, 12px)",
              fontFamily: "'Courier New', monospace",
              textShadow: "0 1px 3px rgba(0,0,0,0.8)",
            }}
          >
            BREAK THE SPIRAL ── REWRITE THE CODE
          </p>
        </div>
      </div>

      {/* ── Scroll indicator ── */}
      <div className="absolute bottom-8 md:bottom-10 left-1/2 -translate-x-1/2 z-30 flex flex-col items-center gap-2">
        <span
          className="text-white/20 tracking-[0.4em] text-[8px]"
          style={{ fontFamily: "'Courier New', monospace" }}
        >
          SCROLL
        </span>
        <div
          className="w-px bg-gradient-to-b from-transparent via-white/40 to-transparent"
          style={{ height: "50px", animation: "scrollLine 3.2s ease-in-out infinite" }}
        />
      </div>

      <style>{`
        @keyframes scrollLine {
          0%   { opacity: 0.1; transform: scaleY(0);    transform-origin: top; }
          50%  { opacity: 0.6; transform: scaleY(1);    transform-origin: top; }
          100% { opacity: 0.1; transform: scaleY(1);    transform-origin: bottom; }
        }
      `}</style>
    </section>
  );
}
